import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'
import { findOrInstallUv } from '../provisioning/uv.js'
import { ensurePython } from '../provisioning/python.js'
import { ensureVenv, installJcodemunchDeps, getVenvPythonPath, getPythonPath } from '../provisioning/jcodemunch.js'
import { registerMcpUserScope, registerMcpProjectScope } from '../registration/mcp.js'
import { deployAssets } from '../registration/assets.js'
import { registerCommands, COMMANDS_GM_DIR } from '../registration/commands.js'
import { registerAgents, AGENTS_DIR } from '../registration/agents.js'
import { installPostCommitHook } from '../provisioning/hooks.js'

export async function runSetup(opts: { project?: boolean }): Promise<void> {
  let uvPath = ''
  let succeeded = 0
  let failed = 0

  // Step 1: Provision uv
  console.log('Provisioning uv...')
  try {
    uvPath = await findOrInstallUv()
    console.log(`  done: uv at ${uvPath}`)
    succeeded++
  } catch (err) {
    console.error(`  error: ${(err as Error).message}`)
    failed++
  }

  // Step 2: Install Python 3.10
  console.log('Installing Python 3.10...')
  if (uvPath) {
    try {
      await ensurePython(uvPath)
      console.log('  done: Python 3.10 installed')
      succeeded++
    } catch (err) {
      console.error(`  error: ${(err as Error).message}`)
      failed++
    }
  } else {
    console.error('  skipped: uv not available')
    failed++
  }

  // Step 3: Create venv and install jCodeMunch deps
  console.log('Installing jCodeMunch dependencies...')
  if (uvPath) {
    try {
      await ensureVenv(uvPath)
      await installJcodemunchDeps(uvPath)
      console.log('  done: jCodeMunch deps installed')
      succeeded++
    } catch (err) {
      console.error(`  error: ${(err as Error).message}`)
      failed++
    }
  } else {
    console.error('  skipped: uv not available')
    failed++
  }

  // Step 4: Register MCP server
  console.log(
    opts.project
      ? 'Registering MCP server (project scope)...'
      : 'Registering MCP server (user scope)...',
  )
  const venvPythonPath = getVenvPythonPath()
  const pythonPath = getPythonPath()
  try {
    if (opts.project) {
      await registerMcpProjectScope(venvPythonPath, pythonPath)
    } else {
      await registerMcpUserScope(venvPythonPath, pythonPath)
    }
    console.log('  done: jcodemunch-mcp registered')
    succeeded++
  } catch (err) {
    const errMsg = (err as Error).message
    console.error(`  error: ${errMsg}`)
    failed++

    // Print manual instructions fallback
    console.log('')
    console.log(`gsd-munch: MCP registration failed: ${errMsg}`)
    console.log('')
    console.log('To register manually, add the following to ~/.claude.json under "mcpServers":')
    console.log(`  "jcodemunch": {`)
    console.log(`    "command": "${venvPythonPath}",`)
    console.log(`    "args": ["-m", "jcodemunch_mcp.server"],`)
    console.log(`    "env": {`)
    console.log(`      "PYTHONPATH": "${pythonPath}",`)
    console.log(`      "JCODEMUNCH_USE_AI_SUMMARIES": "false"`)
    console.log(`    }`)
    console.log(`  }`)
  }

  // Step 5: Deploy GSD workflow assets
  console.log('Deploying GSD workflow assets...')

  // Detect existing GSD installation and inform user about coexistence
  const gsdPath = path.join(os.homedir(), '.claude', 'get-shit-done')
  if (fs.existsSync(gsdPath)) {
    console.log('  Detected existing GSD installation at ~/.claude/get-shit-done/')
    console.log('  Both GSD (/gsd:*) and gsd-munch (/gm:*) can coexist safely.')
  }

  try {
    await deployAssets()
    console.log('  done: workflow assets deployed to ~/.claude/gsd-munch/')
    succeeded++
  } catch (err) {
    console.error(`  error: ${(err as Error).message}`)
    failed++
  }

  // Step 6: Register command stubs
  console.log('Registering command stubs...')
  try {
    await registerCommands()
    const stubCount = fs.existsSync(COMMANDS_GM_DIR)
      ? fs.readdirSync(COMMANDS_GM_DIR).filter(f => f.endsWith('.md')).length
      : 0
    console.log(`  done: ${stubCount} command stubs written to ~/.claude/commands/gm/`)
    succeeded++
  } catch (err) {
    console.error(`  error: ${(err as Error).message}`)
    failed++
  }

  // Step 7: Register agent files
  console.log('Registering agent files...')
  try {
    await registerAgents()
    const agentCount = fs.existsSync(AGENTS_DIR)
      ? fs.readdirSync(AGENTS_DIR).filter(f => f.startsWith('gsd-') && f.endsWith('.md')).length
      : 0
    console.log(`  done: ${agentCount} agent files written to ~/.claude/agents/`)
    succeeded++
  } catch (err) {
    console.error(`  error: ${(err as Error).message}`)
    failed++
  }

  // Step 8: Install post-commit hook for auto re-indexing
  console.log('Installing post-commit hook...')
  try {
    const cwd = process.cwd()
    const result = installPostCommitHook(cwd, venvPythonPath, getPythonPath())
    if (result === 'installed') {
      console.log('  done: post-commit hook installed for auto re-indexing')
      succeeded++
    } else if (result === 'already_present') {
      console.log('  done: post-commit hook already installed')
      succeeded++
    } else {
      console.log('  skipped: not a git repository')
      succeeded++
    }
  } catch (err) {
    console.error(`  error: ${(err as Error).message}`)
    failed++
  }

  // Summary
  const total = succeeded + failed
  console.log('')
  console.log(`Setup complete: ${succeeded}/${total} steps succeeded`)
  if (failed > 0) {
    console.log('Run `gsd-munch doctor` to verify your installation.')
  }
}
